<script setup lang="ts">

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { LinkTextPrimary } from '@shared/ui/links'
import { AppIcon } from '@shared/ui/icons'

import DisciplineProgressReportDetails from './DisciplineProgressReportDetails.vue'
import { useTableDisciplineProgressReports } from '../model/table-discipline-progress-reports.hook'

defineOptions({
  name: 'TableDisciplineProgressReports',
})

const COLUMNS = [
  { title: '#', className: 'table-discipline-progress-reports__id' },
  { title: 'Task due date', className: 'table-discipline-progress-reports__date' },
  { title: 'Discipline', className: '' },
  { title: 'Tutor', className: '' },
  { title: '', className: 'table-discipline-progress-reports__toggle-cell' },
]

const {
  reports,
  isLoading,
  hasReports,
  meta,
  perPageOptions,
  disciplineName,
  disciplineUrl,
  canManageProgressReport,
  getDueDate,
  getTutorName,
  getTutorUrl,
  isExpanded,
  getDetailsRowId,
  toggleDetails,
  openEdit,
  setChangePage,
  setChangePerPage,
} = useTableDisciplineProgressReports()
</script>

<template>
  <div class="table-discipline-progress-reports">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :per-page-options="perPageOptions"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #head>
        <AppTr>
          <AppTh
            v-for="column in COLUMNS"
            :key="column.title"
            :class="column.className"
          >
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              {{ column.title }}
            </AppText>
          </AppTh>
        </AppTr>
      </template>

      <template
        v-if="hasReports"
        #body
      >
        <template
          v-for="report in reports"
          :key="report.id"
        >
          <AppTr>
            <AppTd class="medium table-discipline-progress-reports__id">
              <button
                type="button"
                class="table-discipline-progress-reports__open"
                @click="openEdit(report)"
              >
                <AppText
                  name="body1-medium"
                  color="blue-600"
                >
                  {{ report.id }}
                </AppText>
              </button>
            </AppTd>
            <AppTd class="medium table-discipline-progress-reports__date">
              <AppText name="body1">
                {{ getDueDate(report) }}
              </AppText>
            </AppTd>
            <AppTd class="medium">
              <LinkTextPrimary :href="disciplineUrl">
                {{ disciplineName }}
              </LinkTextPrimary>
            </AppTd>
            <AppTd class="medium">
              <LinkTextPrimary
                v-if="getTutorUrl(report) && getTutorName(report)"
                :href="getTutorUrl(report)!"
              >
                {{ getTutorName(report) }}
              </LinkTextPrimary>
              <AppText
                v-else
                name="body1"
              >
                -
              </AppText>
            </AppTd>
            <AppTd class="zero-padding table-discipline-progress-reports__toggle-cell">
              <button
                type="button"
                class="table-discipline-progress-reports__toggle"
                :class="{ 'table-discipline-progress-reports__toggle--open': isExpanded(report) }"
                :aria-expanded="isExpanded(report)"
                :aria-controls="getDetailsRowId(report)"
                :aria-label="`Toggle details of progress report ${report.id}`"
                @click="toggleDetails(report)"
              >
                <AppIcon
                  :icon="faChevronDown"
                  size="small"
                />
              </button>
            </AppTd>
          </AppTr>

          <AppTr
            v-if="isExpanded(report)"
            :id="getDetailsRowId(report)"
          >
            <AppTd
              class="zero-padding table-discipline-progress-reports__details"
              :colspan="COLUMNS.length"
            >
              <DisciplineProgressReportDetails
                :report="report"
                :editable="canManageProgressReport"
                @click:edit="openEdit(report)"
              />
            </AppTd>
          </AppTr>
        </template>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./TableDisciplineProgressReports.scss" />
